/** Supported async operations for loading copy. */
export type LoadingOperation = 'list' | 'get' | 'create' | 'update' | 'delete';

export interface LoadingMessageInput {
  /** Singular resource noun, e.g. `"answer"`, `"provider"`. */
  resource: string;
  /** Verb describing the in-flight operation. */
  operation: LoadingOperation;
  /** Escape hatch that bypasses template generation. */
  message?: string;
}

/** Irregular plurals for common resource nouns. */
const IRREGULAR_PLURALS: Readonly<Record<string, string>> = {
  answer: 'answers',
  person: 'people',
  child: 'children',
  entry: 'entries',
  category: 'categories',
  row: 'rows',
};

/**
 * Pluralize a singular resource noun for list copy.
 *
 * @param resource - Singular noun from the call site.
 * @returns Plural form used in list messages.
 */
function pluralize(resource: string): string {
  const key = resource.trim().toLowerCase();
  if (IRREGULAR_PLURALS[key]) {
    return IRREGULAR_PLURALS[key];
  }
  if (key.endsWith('y') && key.length > 1 && !/[aeiou]y$/i.test(key)) {
    return `${key.slice(0, -1)}ies`;
  }
  if (key.endsWith('s') || key.endsWith('x') || key.endsWith('ch') || key.endsWith('sh')) {
    return `${key}es`;
  }
  return `${key}s`;
}

/**
 * Build user-facing loading copy from a resource noun and operation.
 *
 * Prefer this over ad-hoc `"Loading…"` strings when the resource and
 * operation are known. Pass the result to `Spinner` `message` / `label`
 * or `Table.ScrollArea` `loadingLabel`.
 *
 * @param input - Resource, operation, and optional override message.
 * @returns Sentence suitable for visible and accessible loading UI.
 *
 * @example
 * loadingMessage({ resource: 'answer', operation: 'list' })
 * // → "Loading answers…"
 *
 * @example
 * loadingMessage({ resource: 'answer', operation: 'get' })
 * // → "Loading answer…"
 *
 * @example
 * loadingMessage({ resource: 'answer', operation: 'create' })
 * // → "Creating answer…"
 */
export function loadingMessage(input: LoadingMessageInput): string {
  if (input.message?.trim()) {
    return input.message.trim();
  }

  const resource = input.resource.trim().toLowerCase() || 'item';
  const plural = pluralize(resource);

  switch (input.operation) {
    case 'list':
      return `Loading ${plural}…`;
    case 'get':
      return `Loading ${resource}…`;
    case 'create':
      return `Creating ${resource}…`;
    case 'update':
      return `Updating ${resource}…`;
    case 'delete':
      return `Deleting ${resource}…`;
    default: {
      const _exhaustive: never = input.operation;
      return _exhaustive;
    }
  }
}
